import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import { Button } from '@mui/material';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import { useDashboardCommentContext } from 'src/components/dashboard/DashboardContext/CommentsContext';
import type {
  SentimentFilter,
  TopicFilter,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';
import useDashboardFilters from 'src/components/dashboard/hooks/useDashboardFilters';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';

export const COMMENTS_LIST_MODAL_DOWNLOAD_BUTTON_TEST_ID =
  'comments-list-modal-download-button';

export const COMMENTS_LIST_MODAL_DOWNLOAD_MESSAGE_TEST_ID =
  'comments-list-modal-download-message';

export interface CommentsListDownloadProps {
  question: string | null;
  filterData?: {
    sentimentFilter?: SentimentFilter;
    topicFilter?: TopicFilter;
  };
  topics: Array<string>;
  isDefaultLanguage: boolean;
}

const CommentsListDownload = ({
  question,
  filterData = {},
  topics,
  isDefaultLanguage,
}: CommentsListDownloadProps) => {
  const {
    i18n: { language },
    t,
  } = useTranslation('dashboard');
  const { textFilter } = useDashboardCommentContext();
  const { moduleId } = useSection();
  const { flatFilters } = useDashboardFilters();
  const { poll } = useDownloadManager();
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (isDisabled) {
      const timer = setTimeout(() => {
        setIsDisabled(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isDisabled]);

  const handleDownloadClick = () => {
    if (!moduleId || !topics.length) return;

    setIsDisabled(true);
    HttpServiceInstance.post(apiPaths.DASHBOARD.COMMENTS_EXPORT, {
      use_translated_text: isDefaultLanguage,
      module_id: moduleId,
      comment_ids: topics.map((topic) => String(topic)),
      language: language,
      dataline_filter: flatFilters,
      question: question || '',
      text_filter: textFilter,
      sentiment_filter: filterData.sentimentFilter,
      topic_filter: filterData.topicFilter,
      source: 'topics',
    }).finally(() => {
      poll();
      setDownloadStarted(true);
    });
  };

  return (
    <>
      <Button
        onClick={handleDownloadClick}
        startIcon={<DownloadRounded />}
        data-test-id={COMMENTS_LIST_MODAL_DOWNLOAD_BUTTON_TEST_ID}
        disabled={isDisabled}
      >
        {t(`OpenCommentsList.DownloadComments`)}
      </Button>

      <SuccessSnackbar
        open={downloadStarted}
        onClose={() => setDownloadStarted(false)}
        testId={COMMENTS_LIST_MODAL_DOWNLOAD_MESSAGE_TEST_ID}
        title={t('downloadManager:DownloadManager')}
        message={t('OpenCommentsList.DownloadMessage')}
      />
    </>
  );
};

export default CommentsListDownload;
